<script setup lang="ts">
import {computed, ref, useTemplateRef} from "vue";
import {CalendarHeart, Pin} from "@lucide/vue";
import {Label} from "@/core/components/ui/label";
import {Switch} from "@/core/components/ui/switch";
import AgendaCalendar from "@/agenda/components/AgendaCalendar.vue";
import {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import {CustomerAppointmentList} from "@/appointment/components/consumer-appointment-timeline";
import {refreshCustomerAppointments} from "@/appointment/composables/useCustomerAppointments";
import Customer from "@/customer/interfaces/Customer";

/**
 * The patient's journey above the pharmacy's calendar, seen from their file.
 *
 * The timeline rereads their appointments at a glance; the whole agenda is
 * shown below — a slot is only worth clicking if the team is free — but every
 * creation opens on this patient, and a switch narrows the grid to their own
 * appointments.
 */
const props = defineProps<{
    customer: Customer;
}>();

const onlyThisPatient = ref(false);

const calendar = useTemplateRef<InstanceType<typeof AgendaCalendar>>('calendar');

const patientName = computed(() => props.customer.information?.name ?? 'Patient anonyme');

const pinnedPatient = computed<SelectedPatient>(() => ({
    id: props.customer.id,
    name: patientName.value,
    phone: props.customer.information?.phone ?? null
}));
</script>

<template>
  <div class="flex flex-1 flex-col gap-3 p-4">
    <!--
      Bounded so a busy journey never pushes the calendar out of the window:
      the timeline scrolls sideways through the days and caps each day.
    -->
    <section class="flex max-h-[40vh] shrink-0 flex-col gap-1 rounded-lg border bg-white px-4 py-3">
      <h2 class="flex items-center gap-2">
        <CalendarHeart class="size-5 text-primary" />
        Rendez-vous du patient
      </h2>
      <CustomerAppointmentList
        :customer="customer"
        class="min-h-0 overflow-y-auto"
        @changed="calendar?.refresh()"
      />
    </section>
    <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-primary-lightest px-3 py-2 text-sm text-gray-700">
      <p class="flex min-w-0 items-center gap-2">
        <Pin class="size-4 shrink-0 text-primary" />
        <span class="truncate">
          Les créneaux cliqués créent un rendez-vous pour <b>{{ patientName }}</b>.
        </span>
      </p>
      <Label class="flex cursor-pointer items-center gap-2 font-normal">
        <Switch v-model="onlyThisPatient" />
        Ce patient uniquement
      </Label>
    </div>
    <!--
      A usable height whatever the window: on a short one the tab scrolls
      rather than crushing the grid.
    -->
    <AgendaCalendar
      ref="calendar"
      class="min-h-[480px] flex-1"
      compact
      :pinned-patient="pinnedPatient"
      :filter-customer-id="onlyThisPatient ? customer.id : null"
      @changed="refreshCustomerAppointments(customer.id)"
    />
  </div>
</template>
